/**
 * Chat History Service
 * Manages saving and loading chat history for Glow Bot
 * All data is stored per-user to ensure chat history is private.
 */

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  lastMessageAt: Date;
}

const STORAGE_KEY_PREFIX = 'glowbot_chat_history';
const MAX_SESSIONS = 50; // Maximum number of saved sessions per user

/** Get user-scoped storage key. Returns empty string if no userId (no persistence). */
const getStorageKey = (userId: string | null | undefined): string => {
  if (!userId) return '';
  return `${STORAGE_KEY_PREFIX}_${userId}`;
};

/**
 * Save a new chat session to localStorage (like ChatGPT creates a new conversation)
 * Scoped to userId - each user's chat history is private.
 */
export const saveChatSession = (sessionId: string, messages: ChatMessage[], userId?: string | null): void => {
  if (messages.length === 0 || !userId) return;

  try {
    const sessions = getChatSessions(userId);

    // Check if session already exists
    const existingIndex = sessions.findIndex(s => s.id === sessionId);
    if (existingIndex !== -1) {
      updateSession(sessionId, messages, userId);
      return;
    }

    // Generate a title from the first user message (or use default)
    const firstUserMessage = messages.find(m => m.isUser);
    const title = firstUserMessage
      ? firstUserMessage.text.slice(0, 50) + (firstUserMessage.text.length > 50 ? '...' : '')
      : 'New Chat';

    const newSession: ChatSession = {
      id: sessionId,
      title,
      messages: messages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      })),
      createdAt: new Date(),
      lastMessageAt: new Date(),
    };

    sessions.unshift(newSession);

    if (sessions.length > MAX_SESSIONS) {
      sessions.splice(MAX_SESSIONS);
    }

    localStorage.setItem(getStorageKey(userId), JSON.stringify(sessions));
  } catch (error) {
    console.error('Error saving chat session:', error);
  }
};

/**
 * Get all chat sessions from localStorage for the given user
 * Returns empty array if userId is missing (ensures no cross-user data)
 */
export const getChatSessions = (userId?: string | null): ChatSession[] => {
  if (!userId) return [];

  try {
    const key = getStorageKey(userId);
    const stored = localStorage.getItem(key);
    if (!stored) return [];

    const sessions = JSON.parse(stored) as ChatSession[];

    return sessions.map(session => ({
      ...session,
      createdAt: new Date(session.createdAt),
      lastMessageAt: new Date(session.lastMessageAt),
      messages: session.messages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      })),
    }));
  } catch (error) {
    console.error('Error loading chat sessions:', error);
    return [];
  }
};

/**
 * Get a specific chat session by ID for the given user
 */
export const getChatSession = (sessionId: string, userId?: string | null): ChatSession | null => {
  const sessions = getChatSessions(userId);
  return sessions.find(s => s.id === sessionId) || null;
};

/**
 * Delete a chat session for the given user
 */
export const deleteChatSession = (sessionId: string, userId?: string | null): void => {
  if (!userId) return;

  try {
    const sessions = getChatSessions(userId);
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(getStorageKey(userId), JSON.stringify(filtered));
  } catch (error) {
    console.error('Error deleting chat session:', error);
  }
};

/**
 * Clear all chat history for the given user
 */
export const clearChatHistory = (userId?: string | null): void => {
  if (!userId) return;

  try {
    localStorage.removeItem(getStorageKey(userId));
  } catch (error) {
    console.error('Error clearing chat history:', error);
  }
};

/**
 * Update an existing session (like ChatGPT - updates the current conversation)
 */
export const updateSession = (sessionId: string, messages: ChatMessage[], userId?: string | null): void => {
  if (messages.length === 0 || !userId) return;

  try {
    const sessions = getChatSessions(userId);
    const sessionIndex = sessions.findIndex(s => s.id === sessionId);

    if (sessionIndex === -1) return;

    const firstUserMessage = messages.find(m => m.isUser);
    const title = firstUserMessage
      ? firstUserMessage.text.slice(0, 50) + (firstUserMessage.text.length > 50 ? '...' : '')
      : sessions[sessionIndex].title;

    sessions[sessionIndex] = {
      ...sessions[sessionIndex],
      title,
      messages: messages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
      })),
      lastMessageAt: new Date(),
    };

    localStorage.setItem(getStorageKey(userId), JSON.stringify(sessions));
  } catch (error) {
    console.error('Error updating session:', error);
  }
};

/**
 * Create a new session and return its ID (like ChatGPT's "New Chat")
 */
export const createNewSession = (): string => {
  const newSessionId = Date.now().toString();
  return newSessionId;
};

